import { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import PhotoUpload from './PhotoUpload';
import { roundRupees } from '../utils/money';
import { getApiBaseUrl } from '../utils/api';
import { readUploadErrorMessage } from '../utils/compressImage';
import {
  isValidModelNumberForSize,
  formatCategoryRangesMessage,
  getCategoryOptionLabel,
  getAvailableModelNumbers,
  formatModelCode,
  MODEL_CATEGORY_RANGES,
  VALID_SIZE_CATEGORIES,
  getPrefixedCategoryRange,
} from '../utils/modelCategoryRanges';

const CATEGORY_ORDER = ['small', 'medium', 'large', 'pen'];
const MAX_OPENING_STOCK = 99;

function resolveCategoryRange(size, categoryRanges) {
  if (!size) return null;
  const fromApi = categoryRanges.find((c) => c.id === size);
  if (fromApi?.min != null && fromApi?.max != null) {
    return { min: fromApi.min, max: fromApi.max };
  }
  const fromConfig = MODEL_CATEGORY_RANGES[size?.toLowerCase()];
  return fromConfig ? { min: fromConfig.min, max: fromConfig.max } : null;
}

async function translateText(text, targetLang) {
  const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=${targetLang}&dt=t&q=${encodeURIComponent(text)}`;
  const res = await fetch(url);
  const data = await res.json();
  return data[0].map(seg => seg[0]).join('');
}

const InventoryModal = ({ isOpen, onClose, onSave, item = null, inventory = [] }) => {
  const [formData, setFormData] = useState({
    modelName: '',
    modelNameMr: '',
    modelNameKn: '',
    modelNumber: '',
    size: '',
    retailPrice: '',
    wholesalePrice: '',
    initialStock: '',
    stockAdjustment: '',
    retailOnly: false,
  });
  const [photo, setPhoto] = useState('');
  const [selectedPhotoFile, setSelectedPhotoFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTranslating, setIsTranslating] = useState(null);
  const [categoryRanges, setCategoryRanges] = useState([]);

  const isEditMode = item !== null;

  const usedNumbers = useMemo(() => {
    return inventory
      .filter((inv) => !isEditMode || inv._id !== item?._id)
      .map((inv) => inv.modelNumber);
  }, [inventory, isEditMode, item?._id]);

  const categoryOptions = useMemo(() => {
    const byId = new Map(categoryRanges.map((c) => [c.id, c]));
    VALID_SIZE_CATEGORIES.forEach((id) => {
      if (!byId.has(id)) {
        const config = MODEL_CATEGORY_RANGES[id];
        if (config) {
          byId.set(id, {
            id,
            name: config.label,
            range: getPrefixedCategoryRange(id),
            min: config.min,
            max: config.max,
          });
        }
      }
    });
    return [...byId.values()].sort(
      (a, b) => CATEGORY_ORDER.indexOf(a.id) - CATEGORY_ORDER.indexOf(b.id)
    );
  }, [categoryRanges]);

  const activeCategoryRange = useMemo(
    () => resolveCategoryRange(formData.size, categoryRanges),
    [formData.size, categoryRanges]
  );

  const availableNumbers = useMemo(() => {
    if (!formData.size || !activeCategoryRange) return [];
    return getAvailableModelNumbers(
      formData.size,
      usedNumbers,
      isEditMode ? formData.modelNumber : null,
      activeCategoryRange
    );
  }, [formData.size, formData.modelNumber, usedNumbers, isEditMode, activeCategoryRange]);

  const categorySlotCount = useMemo(() => {
    if (!activeCategoryRange) return 0;
    return activeCategoryRange.max - activeCategoryRange.min + 1;
  }, [activeCategoryRange]);

  const noNumbersAvailable = Boolean(formData.size) && availableNumbers.length === 0;

  const getCategoryLabel = (id) => {
    const fromOptions = categoryOptions.find((c) => c.id === id);
    if (fromOptions) return `${fromOptions.name} (Model numbers: ${fromOptions.range})`;
    return getCategoryOptionLabel(id);
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    let cancelled = false;

    const loadCategoryRanges = async () => {
      try {
        const baseUrl = getApiBaseUrl();
        const response = await fetch(`${baseUrl}/api/inventory/category-ranges`, {
          credentials: 'include',
        });
        if (response.ok && !cancelled) {
          const data = await response.json();
          setCategoryRanges(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Failed to fetch category ranges:', error);
      }
    };

    loadCategoryRanges();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Populate form when editing
  useEffect(() => {
    if (item) {
      setFormData({
        modelName: item.modelName || '',
        modelNameMr: item.modelNameMr || '',
        modelNameKn: item.modelNameKn || '',
        modelNumber: item.modelNumber || '',
        size: item.size || '',
        retailPrice: item.retailPrice || item.price || '',
        wholesalePrice: item.wholesalePrice || '',
        initialStock: item.initialStock || '',
        stockAdjustment: '',
        retailOnly: Boolean(item.retailOnly),
      });
      setPhoto(item.photo || '');
      setSelectedPhotoFile(null);
    } else {
      // Reset form for new item
      setFormData({
        modelName: '',
        modelNameMr: '',
        modelNameKn: '',
        modelNumber: '',
        size: '',
        retailPrice: '',
        wholesalePrice: '',
        initialStock: '',
        stockAdjustment: '',
        retailOnly: false,
      });
      setPhoto('');
      setSelectedPhotoFile(null);
    }
    setErrors({});
  }, [item, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSizeChange = (e) => {
    const size = e.target.value;
    if (!size) {
      setFormData((prev) => ({ ...prev, size: '', modelNumber: '' }));
      return;
    }

    const range = resolveCategoryRange(size, categoryRanges);
    const nextAvailable = getAvailableModelNumbers(
      size,
      usedNumbers,
      isEditMode ? formData.modelNumber : null,
      range
    );
    setFormData((prev) => ({
      ...prev,
      size,
      modelNumber: isEditMode
        ? (nextAvailable.includes(Number(prev.modelNumber)) ? prev.modelNumber : (nextAvailable[0] != null ? String(nextAvailable[0]) : ''))
        : (nextAvailable[0] != null ? String(nextAvailable[0]) : ''),
    }));

    if (errors.size || errors.modelNumber) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.size;
        delete newErrors.modelNumber;
        return newErrors;
      });
    }
  };
  const handlePhotoFileSelected = (file) => {
    setSelectedPhotoFile(file);
  };

  const uploadPhotoForNewModel = async (inventoryId) => {
    if (!selectedPhotoFile || !inventoryId) return;

    try {
      const formData = new FormData();
      formData.append('photo', selectedPhotoFile);

      const baseUrl = getApiBaseUrl();
      const response = await fetch(`${baseUrl}/api/inventory-photos/upload/${inventoryId}`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        toast.success('Photo uploaded successfully!');
      } else {
        const message = await readUploadErrorMessage(response);
        toast.error(message);
      }
    } catch (error) {
      console.error('Error uploading photo for new model:', error);
      toast.error(error.message || 'Failed to upload photo');
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.modelName.trim()) {
      newErrors.modelName = 'Model name is required';
    }

    if (!formData.modelNumber || formData.modelNumber === '') {
      newErrors.modelNumber = 'Model number is required';
    } else if (noNumbersAvailable && !isEditMode) {
      newErrors.modelNumber = 'No model numbers available in this category';
    }
    if (!formData.size) {
      newErrors.size = 'Category (size) is required';
    }

    // Validate model number range based on size
    if (formData.size && formData.modelNumber) {
      const num = parseInt(formData.modelNumber);
      if (isNaN(num)) {
        newErrors.modelNumber = 'Model number must be a valid number';
      } else if (!isValidModelNumberForSize(num, formData.size)) {
          newErrors.modelNumber = `Model number ${num} is not valid for ${formData.size} category. ${formatCategoryRangesMessage()}`;
        }
    }

    if (formData.retailPrice === '' || Number(formData.retailPrice) < 0) {
      newErrors.retailPrice = 'Retail price must be greater than or equal to 0';
    }

    if (!formData.retailOnly) {
      if (formData.wholesalePrice === '' || Number(formData.wholesalePrice) < 0) {
        newErrors.wholesalePrice = 'Wholesale price must be greater than or equal to 0';
      } else if (Number(formData.wholesalePrice) > Number(formData.retailPrice)) {
        newErrors.wholesalePrice = 'Wholesale price should not be higher than retail price';
      }
    }

    if (!isEditMode) {
      const stock = Number(formData.initialStock);
      if (
        formData.initialStock === '' ||
        !Number.isInteger(stock) ||
        stock < 0 ||
        stock > MAX_OPENING_STOCK
      ) {
        newErrors.initialStock = 'Opening stock must be between 0 and 99';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    await performSave(formData);
  };

  const performSave = async (formDataToSave) => {
    setIsSubmitting(true);

    try {
      const retailOnly = Boolean(formDataToSave.retailOnly);
      const wholesalePrice = retailOnly && (formDataToSave.wholesalePrice === '' || formDataToSave.wholesalePrice == null)
        ? 0
        : roundRupees(formDataToSave.wholesalePrice);

      if (isEditMode) {
        // Update existing item
        const updateData = {
          modelName: formDataToSave.modelName.trim(),
          modelNameMr: (formDataToSave.modelNameMr || '').trim(),
          modelNameKn: (formDataToSave.modelNameKn || '').trim(),
          modelNumber: parseInt(formDataToSave.modelNumber),
          size: formDataToSave.size.toLowerCase(),
          retailPrice: roundRupees(formDataToSave.retailPrice),
          wholesalePrice,
          retailOnly,
        };

        // Only include stockAdjustment if provided
        if (formDataToSave.stockAdjustment !== '') {
          updateData.stockAdjustment = Number(formDataToSave.stockAdjustment);
        }

        await onSave(item._id, updateData);
      } else {
        // Create new item
        const newItem = {
          modelName: formDataToSave.modelName.trim(),
          modelNameMr: (formDataToSave.modelNameMr || '').trim(),
          modelNameKn: (formDataToSave.modelNameKn || '').trim(),
          modelNumber: parseInt(formDataToSave.modelNumber),
          size: formDataToSave.size.toLowerCase(),
          retailPrice: roundRupees(formDataToSave.retailPrice),
          wholesalePrice,
          initialStock: Number(formDataToSave.initialStock),
          retailOnly,
        };

        const result = await onSave(null, newItem);
        // Upload photo if one was selected
        if (selectedPhotoFile && result && result._id) {
          await uploadPhotoForNewModel(result._id);
        }
      }

      // Reset form and close modal
      setFormData({
        modelName: '',
        modelNameMr: '',
        modelNameKn: '',
        modelNumber: '',
        size: '',
        retailPrice: '',
        wholesalePrice: '',
        initialStock: '',
        stockAdjustment: '',
        retailOnly: false,
      });
      onClose();
    } catch (error) {
      // Error handling is done in parent component
      console.error('Error saving inventory:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-800">
              {isEditMode ? 'Edit Inventory Item' : 'Add New Model'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              {/* Model Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model Name *
                </label>
                <input
                  type="text"
                  name="modelName"
                  value={formData.modelName}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    errors.modelName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., Small Traditional Ganesh"
                />
                {errors.modelName && (
                  <p className="text-red-500 text-sm mt-1">{errors.modelName}</p>
                )}
              </div>

              {/* Marathi Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Marathi Name <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="modelNameMr"
                    value={formData.modelNameMr}
                    onChange={handleChange}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="मराठी नाव"
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      if (!formData.modelName.trim()) return;
                      setIsTranslating('mr');
                      try {
                        const mr = await translateText(formData.modelName.trim(), 'mr');
                        setFormData(prev => ({ ...prev, modelNameMr: mr }));
                      } catch {
                        // silently fail — admin can type manually
                      } finally {
                        setIsTranslating(null);
                      }
                    }}
                    disabled={isTranslating !== null || !formData.modelName.trim()}
                    className="px-3 py-2 bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-medium rounded-lg hover:bg-indigo-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isTranslating === 'mr' ? 'Translating…' : 'Auto-translate'}
                  </button>
                </div>
                <p className="text-gray-400 text-xs mt-1">
                  Enter English name above, then click &quot;Auto-translate&quot; — edit the result if needed.
                </p>
              </div>

              {/* Kannada Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kannada Name <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="modelNameKn"
                    value={formData.modelNameKn}
                    onChange={handleChange}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="ಕನ್ನಡ ಹೆಸರು"
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      if (!formData.modelName.trim()) return;
                      setIsTranslating('kn');
                      try {
                        const kn = await translateText(formData.modelName.trim(), 'kn');
                        setFormData(prev => ({ ...prev, modelNameKn: kn }));
                      } catch {
                        // silently fail — admin can type manually
                      } finally {
                        setIsTranslating(null);
                      }
                    }}
                    disabled={isTranslating !== null || !formData.modelName.trim()}
                    className="px-3 py-2 bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-medium rounded-lg hover:bg-indigo-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isTranslating === 'kn' ? 'Translating…' : 'Auto-translate to Kannada'}
                  </button>
                </div>
                <p className="text-gray-400 text-xs mt-1">
                  Enter English name above, then click &quot;Auto-translate to Kannada&quot; — edit the result if needed.
                </p>
              </div>

              {/* Size/Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category (Size) *
                </label>
                <select
                  name="size"
                  value={formData.size}
                  onChange={handleSizeChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    errors.size ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select category</option>
                  {categoryOptions.map((category) => (
                    <option key={category.id} value={category.id}>
                      {getCategoryLabel(category.id)}
                    </option>
                  ))}
                </select>
                {errors.size && (
                  <p className="text-red-500 text-sm mt-1">{errors.size}</p>
                )}
                <p className="text-gray-500 text-xs mt-1">
                  Choose category first to see available model numbers
                </p>
              </div>

              {/* Model Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model Number *
                </label>
                <select
                  name="modelNumber"
                  value={formData.modelNumber}
                  onChange={handleChange}
                  disabled={!formData.size || noNumbersAvailable}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                    errors.modelNumber ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">
                    {!formData.size
                      ? 'Select category first'
                      : noNumbersAvailable
                        ? 'No numbers available'
                        : 'Select model number'}
                  </option>
                  {availableNumbers.map((num) => (
                    <option key={num} value={num}>
                      {formatModelCode(formData.size, num)}
                    </option>
                  ))}
                </select>
                {errors.modelNumber && (
                  <p className="text-red-500 text-sm mt-1">{errors.modelNumber}</p>
                )}
                {formData.size && !noNumbersAvailable && (
                  <p className="text-gray-500 text-xs mt-1">
                    {availableNumbers.length} of {categorySlotCount} available in this category
                  </p>
                )}
                {noNumbersAvailable && (
                  <p className="text-amber-600 text-xs mt-1">
                    All model numbers in this category are already used.
                  </p>
                )}
              </div>

              {/* Retail Price */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Retail Price (₹) *
                </label>
                <input
                  type="number"
                  name="retailPrice"
                  value={formData.retailPrice}
                  onChange={handleChange}
                  min="0"
                  step="1"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    errors.retailPrice ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="0.00"
                />
                {errors.retailPrice && (
                  <p className="text-red-500 text-sm mt-1">{errors.retailPrice}</p>
                )}
                <p className="text-gray-500 text-xs mt-1">
                  Price for individual retail customers
                </p>
              </div>

              {/* Retail only — check first so wholesale can be skipped */}
              <div className="md:col-span-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="retailOnly"
                    checked={formData.retailOnly}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFormData((prev) => ({ ...prev, retailOnly: checked }));
                      if (checked && errors.wholesalePrice) {
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next.wholesalePrice;
                          return next;
                        });
                      }
                    }}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-700">Retail only</span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Exclude from wholesale orders only (still visible in shop at retail price). Wholesale price is not required.
                    </span>
                  </span>
                </label>
              </div>

              {/* Wholesale Price — not required when retail only */}
              {!formData.retailOnly && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Wholesale Price (₹) *
                </label>
                <input
                  type="number"
                  name="wholesalePrice"
                  value={formData.wholesalePrice}
                  onChange={handleChange}
                  min="0"
                  step="1"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                    errors.wholesalePrice ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="0.00"
                />
                {errors.wholesalePrice && (
                  <p className="text-red-500 text-sm mt-1">{errors.wholesalePrice}</p>
                )}
                <p className="text-gray-500 text-xs mt-1">
                  Price for bulk wholesale orders (should be ≤ retail price)
                </p>
              </div>
              )}

              {/* Stock Management */}
              {!isEditMode ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Opening Stock *
                  </label>
                  <input
                    type="number"
                    name="initialStock"
                    value={formData.initialStock}
                    onChange={handleChange}
                    min="0"
                    max="99"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      errors.initialStock ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0"
                  />
                  <p className="text-gray-500 text-xs mt-1">
                    Set once when creating the model (0–99). Cannot be changed later.
                  </p>
                  {errors.initialStock && (
                    <p className="text-red-500 text-sm mt-1">{errors.initialStock}</p>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                    <p className="text-xs text-gray-500">Opening Stock (fixed)</p>
                    <p className="text-lg font-semibold text-gray-800">{item?.initialStock ?? 0}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Stock Adjustment
                    </label>
                    <input
                      type="number"
                      name="stockAdjustment"
                      value={formData.stockAdjustment}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="+10 receive stock, −5 remove (leave empty for no change)"
                    />
                    <p className="text-gray-500 text-xs mt-1">
                      On hand: {item?.currentStock || 0} · Sold (from orders): {item?.sold ?? 0}
                      <br />
                      <strong>+ value</strong> = receive new units (current stock only).
                      <strong> − value</strong> = remove from shelf (does not change sold).
                    </p>
                  </div>
                </div>
              )}

              {/* Photo Upload */}
              <PhotoUpload
                inventoryId={item?._id || null}
                currentPhoto={photo}
                onPhotoUpdate={setPhoto}
                onFileSelected={handlePhotoFileSelected}
                isNewModel={!isEditMode}
              />
            </div>

            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="admin-btn admin-btn-modal-confirm admin-btn-neutral flex-1 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting || (!isEditMode && noNumbersAvailable)}
              >
                {isSubmitting ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InventoryModal;
